import React from "react";
import { TransitionGroup } from "react-transition-group";
import { t } from "ttag";

import { CSSTransitionWithRef } from "../../../components/CssTransition";
import { ErrorModal } from "../../../components/ErrorModal";
import { AddToCartType, RemoveFromCartType } from "../../../constants";
import {
    IBasket,
    IBasketLine,
    IOfferDiscount,
    IVoucherDiscount,
} from "../../../models/catalogue";
import { FinancingPlan } from "../../../models/financing";
import { IBasketLineAPIURL, IProductAPIURL } from "../../../models/nominals";
import { isBasketLineStockError } from "../../../store/basket/thunks";
import {
    trackAddToBasketEvent,
    trackRemoveFromBasketEvent,
} from "../../../utils/analytics";
import { getHighestDiscountName } from "../../../utils/money";
import {
    BasketLineFull,
    BasketLineMinimal,
    BasketLineMinimalEnhanced,
    TGetProductUpsell,
} from "../BasketLine";
import {
    OfferLineFull,
    OfferLineMinimal,
    OfferLineMinimalEnhanced,
    VoucherLineFull,
    VoucherLineMinimal,
    VoucherLineMinimalEnhanced,
} from "../VirtualLine";
import {
    BasketLineVariant,
    PredictedDeliveryDateDisplayType,
    PreferredDeliveryDateDisplayType,
    VIRTUAL_BASKET_LINE_BENEFIT_TYPE,
} from "../constants";

import styles from "./BasketLines.module.scss";

interface IProps {
    basket: IBasket | null;
    financingPlans: readonly FinancingPlan[];
    variant: BasketLineVariant;
    shippingMethodCode?: string;
    getProductUpsell: TGetProductUpsell;
    showValueProps: boolean;
    predictedDeliveryDates: PredictedDeliveryDateDisplayType;
    preferredDeliveryDates: PreferredDeliveryDateDisplayType;
    loadConcreteBundles: (url: IProductAPIURL) => void;
    addBasketLine: (
        productURL: IProductAPIURL,
        qty?: number,
    ) => Promise<IBasket>;
    updateBasketLineQuantity: (
        lineURL: IBasketLineAPIURL,
        newQty: number,
    ) => Promise<IBasket>;
    removeBasketLine: (lineURL: IBasketLineAPIURL) => Promise<IBasket>;
    isMobile: boolean;
    deliveryIsFree?: boolean;
    removeVoucherCode: (code: string) => void;
}

const StandardLine = (
    props: IProps & {
        basket: IBasket;
        line: IBasketLine;
        onStockError: () => void;
    },
): React.ReactNode => {
    const onRemoveLine = async (line: IBasketLine) => {
        await props.removeBasketLine(line.url);
        trackRemoveFromBasketEvent(
            line.product,
            line.quantity,
            RemoveFromCartType.REMOVED,
            getHighestDiscountName(line.discounts),
        );
    };

    const onUpdateLineQuantity = async (
        event: React.FormEvent<HTMLSelectElement>,
        line: IBasketLine,
    ) => {
        if (!line.quantity) {
            return;
        }

        const lineURL = line.url;
        const newQty = parseInt(event.currentTarget.value, 10);
        const difference = newQty - line.quantity;

        if (newQty === 0) {
            await props.removeBasketLine(lineURL);
            trackRemoveFromBasketEvent(
                line.product,
                line.quantity,
                RemoveFromCartType.REMOVED,
                getHighestDiscountName(line.discounts),
            );
        } else {
            try {
                await props.updateBasketLineQuantity(lineURL, newQty);
            } catch (err) {
                // The server refused the quantity (exceeds stock, #34678). The
                // select is controlled by the unchanged line, so it snaps
                // back on its own; tell the shopper why.
                if (isBasketLineStockError(err)) {
                    props.onStockError();
                    return;
                }
                throw err;
            }
            // When the user increases the quantity of an item already in their basket,
            // we should fire add_to_cart event with a type of `increase quantity`
            if (difference > 0) {
                trackAddToBasketEvent(
                    line.product,
                    Math.abs(difference),
                    AddToCartType.INCREASE_QUANTITY,
                    getHighestDiscountName(line.discounts),
                );
            } else if (difference < 0) {
                trackRemoveFromBasketEvent(
                    line.product,
                    Math.abs(difference),
                    RemoveFromCartType.DECREASE_QUANTITY,
                    getHighestDiscountName(line.discounts),
                );
            }
        }
    };

    switch (props.variant) {
        case BasketLineVariant.MINIMAL:
            return <BasketLineMinimal {...props} line={props.line} />;
        case BasketLineVariant.MINIMAL_ENHANCED:
            return (
                <BasketLineMinimalEnhanced
                    {...props}
                    onRemoveLine={() => {
                        onRemoveLine(props.line);
                    }}
                    onUpdateLineQuantity={(e) => {
                        onUpdateLineQuantity(e, props.line);
                    }}
                    line={props.line}
                />
            );
        case BasketLineVariant.FULL:
            return (
                <BasketLineFull
                    {...props}
                    onRemoveLine={() => {
                        onRemoveLine(props.line);
                    }}
                    onUpdateLineQuantity={(e) => {
                        onUpdateLineQuantity(e, props.line);
                    }}
                    line={props.line}
                    basket={props.basket}
                    isMobile={props.isMobile}
                    deliveryIsFree={props.deliveryIsFree}
                />
            );
    }
};

const VoucherLine = (
    props: IProps & {
        voucher: IVoucherDiscount;
    },
): React.ReactNode => {
    switch (props.variant) {
        case BasketLineVariant.MINIMAL:
            return (
                <VoucherLineMinimal
                    voucherData={props.voucher}
                    onRemoveLine={props.removeVoucherCode}
                />
            );
        case BasketLineVariant.MINIMAL_ENHANCED:
            return (
                <VoucherLineMinimalEnhanced
                    voucherData={props.voucher}
                    onRemoveLine={props.removeVoucherCode}
                />
            );
        case BasketLineVariant.FULL:
            return (
                <VoucherLineFull
                    voucherData={props.voucher}
                    onRemoveLine={props.removeVoucherCode}
                />
            );
    }
};

const OfferLine = (
    props: IProps & {
        offer: IOfferDiscount;
    },
): React.ReactNode => {
    switch (props.variant) {
        case BasketLineVariant.MINIMAL:
            return <OfferLineMinimal offerData={props.offer} />;
        case BasketLineVariant.MINIMAL_ENHANCED:
            return <OfferLineMinimalEnhanced offerData={props.offer} />;
        case BasketLineVariant.FULL:
            return <OfferLineFull offerData={props.offer} />;
    }
};

const LineList = (
    props: IProps & {
        basket: IBasket;
        onStockError: () => void;
    },
): React.ReactNode => (
    <>
        {props.basket.lines
            // Exclude any line with a parent (any child line) from the line
            // list. See #27850 (re product bundles).
            .filter((line) => !line.parent)
            .map((line) => (
                <CSSTransitionWithRef
                    key={`${line.url}`}
                    classNames="transition--fade"
                    timeout={{ exit: 500, enter: 500 }}
                >
                    <StandardLine {...props} line={line} />
                </CSSTransitionWithRef>
            ))}
        {props.basket.voucher_post_order_actions
            .filter(
                (voucherData) =>
                    voucherData.benefit_type ===
                        VIRTUAL_BASKET_LINE_BENEFIT_TYPE &&
                    voucherData.benefit_value !== null,
            )
            .map((voucherData) => (
                <CSSTransitionWithRef
                    key={voucherData.voucher.code}
                    classNames="transition--fade"
                    timeout={{ exit: 500, enter: 500 }}
                >
                    <VoucherLine {...props} voucher={voucherData} />
                </CSSTransitionWithRef>
            ))}
        {props.basket.offer_post_order_actions
            .filter(
                (offerData) =>
                    offerData.benefit_type ===
                        VIRTUAL_BASKET_LINE_BENEFIT_TYPE &&
                    offerData.benefit_value !== null,
            )
            .map((offerData) => (
                <CSSTransitionWithRef
                    key={offerData.index}
                    classNames="transition--fade"
                    timeout={{ exit: 500, enter: 500 }}
                >
                    <OfferLine {...props} offer={offerData} />
                </CSSTransitionWithRef>
            ))}
    </>
);

const EmptyBasket = () => (
    <CSSTransitionWithRef
        key="empty"
        classNames="transition--fade"
        timeout={{ exit: 500, enter: 500 }}
    >
        <p>{t`Your cart is empty.`}</p>
    </CSSTransitionWithRef>
);

export const BasketLines = (props: IProps): React.JSX.Element => {
    const [stockErrorOpen, setStockErrorOpen] = React.useState(false);
    return (
        <>
            <TransitionGroup>
                <ul className={styles.basketLinesList}>
                    {props.basket && props.basket.lines.length > 0 ? (
                        <LineList
                            {...props}
                            basket={props.basket}
                            onStockError={() => setStockErrorOpen(true)}
                        />
                    ) : (
                        <EmptyBasket />
                    )}
                </ul>
            </TransitionGroup>
            <ErrorModal
                name="basket-line-quantity"
                isOpen={stockErrorOpen}
                onRequestClose={() => setStockErrorOpen(false)}
            >
                <p>
                    {t`You've selected more than the available inventory for this item. Please update your quantity to continue.`}
                </p>
            </ErrorModal>
        </>
    );
};
